import { POSTHOG_FEATURE_FLAGS, useFeatureFlagsReady, usePostHogFeatureFlag } from '../usePostHogFeatureFlag'
import { usePermissions } from '../usePermissions'

/**
 * Shared FGA gating for the `validation_requests` domain.
 *
 * While `internal_fga_orgv_validation_requests` is off the backend skips its permission
 * check, so every check here falls open to the pre-FGA behaviour — the same window
 * handling as `AccessRoute` and `resolveNavAccess`.
 *
 * `canAccept`/`canReject` sit at `approver` while `canQc` sits on the parallel
 * `qc_reviewer` branch, so they are kept apart: a QC reviewer can neither accept nor
 * reject, and an approver cannot submit QC.
 *
 * Every check waits for `useFeatureFlagsReady()`. `usePostHogFeatureFlag` reads an
 * unresolved flag as `false`, which would fall open and render controls that then vanish
 * once the flag lands as on. On `/organizations` that was more than cosmetic: clicking
 * Add organisation inside that window unmounted the modal and lost the form.
 */
export function useValidationRequestsAccess(): {
  canRead: boolean
  canCreate: boolean
  canUpdate: boolean
  canAccept: boolean
  canReject: boolean
  canQc: boolean
} {
  const { userHasPermissions } = usePermissions()
  const flagsReady = useFeatureFlagsReady()
  const internalFgaOrgvValidationRequestsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_REQUESTS
  )

  const isAllowed = (permission: string) =>
    flagsReady && (!internalFgaOrgvValidationRequestsFeatureFlag || userHasPermissions(permission))

  return {
    canRead: isAllowed('validation_requests:can_read'),
    canCreate: isAllowed('validation_requests:can_create'),
    canUpdate: isAllowed('validation_requests:can_update'),
    canAccept: isAllowed('validation_requests:can_accept'),
    canReject: isAllowed('validation_requests:can_reject'),
    canQc: isAllowed('validation_requests:can_qc')
  }
}
